import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useScreenWidth } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  generatePath,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import { RestrictedByCapability } from "../../auth/RestrictedByCapability.js";
import { RestrictedByDocumentPermission } from "../../auth/RestrictedByDocumentPermission.js";
import { useUserData } from "../../auth/useUserData.js";
import { usePageWidth } from "../../commons/hooks/usePageWidth.js";
import { BreadcrumbItem } from "../../commons/layout/PageBreadcrumbs.js";
import { PageTopbarItem } from "../../commons/layout/PageSubmenu.js";
import { PageNotFound } from "../../commons/PageNotFound.js";
import { SelectInfoMessageItem } from "../../features/doc-items/items/SelectInfoMessageItem.js";
import {
  useTreeItemIdsWithWizardQuestions,
  useWizardContext,
} from "../../features/documents/useWizardContext.js";
import { ProjectQuoteCalculation } from "./ProjectQuoteCalculation.js";
import { ProjectQuoteContext } from "./ProjectQuoteContext.js";
import { ProjectQuoteDataGrid } from "./ProjectQuoteDataGrid.js";
import { ProjectQuoteEdit } from "./ProjectQuoteEdit.js";
import { ProjectQuoteEditItem } from "./ProjectQuoteEditItem.js";
import { ProjectQuotePreview } from "./ProjectQuotePreview.js";
import { ProjectQuoteProperties } from "./ProjectQuoteProperties.js";
import { ProjectQuoteThreeD } from "./ProjectQuoteThreeD.js";
import { ProjectQuoteTroubleshoot } from "./ProjectQuoteTroubleshoot.js";
import { useProjectQuoteRoutesQuery } from "./ProjectRoutes.generated.js";

export const ProjectQuoteRoutes = ({
  projectId,
  prefixBreadcrumbs,
  submenuItems,
  activeSubmenuItem,
}: {
  projectId: string;
  prefixBreadcrumbs: BreadcrumbItem[];
  submenuItems: PageTopbarItem[];
  activeSubmenuItem: PageTopbarItem | undefined;
}) => {
  const { quoteId } = useParams();
  if (!quoteId) throw new Error("Quote id is missing");

  const viewer = useUserData().currentUser!;
  const { t } = useTranslate(["ProjectPageTopbar", "Quotes"]);
  const { isMinTablet } = useScreenWidth();
  const { isMinOneColumnWithPreview, isMinTwoColumnsWithPreview } =
    usePageWidth();

  const client = useApolloClient();
  const query = useProjectQuoteRoutesQuery({
    client,
    variables: {
      projectId,
      quoteId,
    },
  });

  const quote = getDataOrNull(query.data?.quote)?.quote;
  const canViewEdit = quote?.viewerPermissions.includes("MANAGE_QUOTES");
  const redirectPath = canViewEdit
    ? `/projects/:projectId/quotes/:quoteId/edit`
    : `/projects/:projectId/quotes/:quoteId/preview`;

  const {
    activeContext: activeWizardContext,
    selectContextButton: selectWizardContextButton,
  } = useWizardContext(quote ?? { viewerPermissions: [] });
  const [hasItemAnyQuestions, refetchWizardQuestions] =
    useTreeItemIdsWithWizardQuestions({
      sourceDoc: {
        type: "quote",
        quoteId: quoteId,
        contractorId: viewer.organisation.id,
      },
      wizardContext: activeWizardContext,
    });

  const breadcrumbs = React.useMemo(() => {
    const items = [
      ...prefixBreadcrumbs,
      {
        title: t("Quotes", {
          ns: "ProjectPageTopbar",
        }),
        to: `/projects/${projectId}/quotes`,
      },
    ];

    if (viewer.organisation.capabilities.includes("QUOTING")) {
      items.push({
        title: canViewEdit
          ? t("Outgoing", {
              ns: "Quotes",
            })
          : t("Incoming", {
              ns: "Quotes",
            }),
        to: `/projects/${projectId}/quotes?tab=${canViewEdit ? "OUTGOING" : "INCOMING"}`,
      });
    }

    items.push({
      title: quote?.title ?? "",
      to: `/projects/${projectId}/quotes/${quoteId}`,
    });

    return items;
  }, [
    prefixBreadcrumbs,
    t,
    projectId,
    viewer.organisation.capabilities,
    quote?.title,
    quoteId,
    canViewEdit,
  ]);

  const [dismissedAnnotationIds, setDismissedAnnotationIds] = React.useState<
    ReadonlySet<string>
  >(() => new Set());
  const dismissAnnotation = React.useCallback((annotationId: string) => {
    setDismissedAnnotationIds(prev => {
      const next = new Set(prev);
      next.add(annotationId);
      return next;
    });
  }, []);
  const restoreAnnotations = React.useCallback(() => {
    setDismissedAnnotationIds(new Set());
  }, []);
  React.useEffect(() => {
    setDismissedAnnotationIds(new Set());
  }, [quoteId]);

  const quoteAnnotations = React.useMemo(
    () =>
      (quote?.annotations ?? []).filter(a => !dismissedAnnotationIds.has(a.id)),
    [quote?.annotations, dismissedAnnotationIds]
  );
  const hiddenAnnotationCount = React.useMemo(
    () =>
      (quote?.annotations ?? []).filter(a => dismissedAnnotationIds.has(a.id))
        .length,
    [quote?.annotations, dismissedAnnotationIds]
  );

  if (!quote) return null;

  return (
    <ProjectQuoteContext.Provider
      value={{
        projectId,
        quoteId,
        activeWizardContext,
        selectWizardContextButton,
        hasItemAnyQuestions,
        refetchWizardQuestions,
        viewerPermissions: quote.viewerPermissions,
        quoteAnnotations,
        dismissAnnotation,
        hiddenAnnotationCount,
        restoreAnnotations,
      }}
    >
      <Routes>
        <Route
          index
          element={
            <Navigate
              to={generatePath(redirectPath, {
                projectId,
                quoteId,
              })}
              replace
            />
          }
        />

        <Route
          path={`preview`}
          element={
            <RestrictedByDocumentPermission
              permission="READ_QUOTES"
              document={quote}
            >
              <ProjectQuotePreview
                projectId={projectId}
                quoteId={quoteId}
                prefixBreadcrumbs={breadcrumbs}
                submenuItems={submenuItems}
                activeSubmenuItem={activeSubmenuItem}
              />
            </RestrictedByDocumentPermission>
          }
        />
        <Route
          path={`edit`}
          element={
            <RestrictedByDocumentPermission
              permission="MANAGE_QUOTES"
              document={quote}
            >
              <ProjectQuoteEdit
                projectId={projectId}
                quoteId={quoteId}
                prefixBreadcrumbs={breadcrumbs}
                submenuItems={submenuItems}
                activeSubmenuItem={activeSubmenuItem}
              />
            </RestrictedByDocumentPermission>
          }
        >
          {isMinTablet &&
            (isMinTwoColumnsWithPreview || !isMinOneColumnWithPreview) && (
              <Route index element={<SelectInfoMessageItem />} />
            )}
          <Route
            path={"items/:itemId"}
            element={
              <ProjectQuoteEditItem
                projectId={projectId}
                quoteId={quoteId}
                pathToDocList={`/projects/${projectId}/quotes`}
                pathToDocPage={`/projects/${projectId}/quotes/${quoteId}/edit`}
              />
            }
          />
        </Route>
        <Route
          path={`calculation`}
          element={
            !isMinTablet ? (
              <Navigate
                to={`/projects/${projectId}/quotes/${quoteId}/edit`}
                replace
              />
            ) : (
              <RestrictedByDocumentPermission
                permission="MANAGE_QUOTES"
                document={quote}
              >
                <ProjectQuoteCalculation
                  projectId={projectId}
                  quoteId={quoteId}
                  prefixBreadcrumbs={breadcrumbs}
                  submenuItems={submenuItems}
                  activeSubmenuItem={activeSubmenuItem}
                />
              </RestrictedByDocumentPermission>
            )
          }
        >
          <Route
            path={"items/:itemId"}
            element={
              <ProjectQuoteEditItem
                projectId={projectId}
                quoteId={quoteId}
                pathToDocList={`/projects/${projectId}/quotes`}
                pathToDocPage={`/projects/${projectId}/quotes/${quoteId}/calculation`}
                isAbsolute={false}
              />
            }
          />
        </Route>
        <Route
          path={`grid`}
          element={
            !isMinTablet ? (
              <Navigate
                to={`/projects/${projectId}/quotes/${quoteId}/edit`}
                replace
              />
            ) : (
              <RestrictedByDocumentPermission
                permission="MANAGE_QUOTES"
                document={quote}
              >
                <ProjectQuoteDataGrid
                  projectId={projectId}
                  quoteId={quoteId}
                  prefixBreadcrumbs={breadcrumbs}
                  submenuItems={submenuItems}
                  activeSubmenuItem={activeSubmenuItem}
                />
              </RestrictedByDocumentPermission>
            )
          }
        >
          <Route
            path={"items/:itemId"}
            element={
              <ProjectQuoteEditItem
                projectId={projectId}
                quoteId={quoteId}
                pathToDocList={`/projects/${projectId}/quotes`}
                pathToDocPage={`/projects/${projectId}/quotes/${quoteId}/grid`}
                isAbsolute={true}
                position="right"
              />
            }
          />
        </Route>
        <Route
          path={`3d`}
          element={
            <RestrictedByDocumentPermission
              permission="READ_QUOTES"
              document={quote}
            >
              <ProjectQuoteThreeD
                projectId={projectId}
                quoteId={quoteId}
                prefixBreadcrumbs={breadcrumbs}
                submenuItems={submenuItems}
                activeSubmenuItem={activeSubmenuItem}
              />
            </RestrictedByDocumentPermission>
          }
        >
          <Route
            path={"items/:itemId"}
            element={
              <ProjectQuoteEditItem
                projectId={projectId}
                quoteId={quoteId}
                pathToDocList={`/projects/${projectId}/quotes`}
                pathToDocPage={`/projects/${projectId}/quotes/${quoteId}/3d`}
                isAbsolute={true}
              />
            }
          />
        </Route>
        <Route
          path={`troubleshoot`}
          element={
            <RestrictedByCapability capability="EXPRESSIONS">
              <RestrictedByDocumentPermission
                permission="MANAGE_QUOTES"
                document={quote}
              >
                <ProjectQuoteTroubleshoot
                  projectId={projectId}
                  quoteId={quoteId}
                  prefixBreadcrumbs={breadcrumbs}
                  submenuItems={submenuItems}
                  activeSubmenuItem={activeSubmenuItem}
                  pathToDocList={`/projects/${projectId}/quotes`}
                  pathToDoc={`/projects/${projectId}/quotes/${quoteId}`}
                />
              </RestrictedByDocumentPermission>
            </RestrictedByCapability>
          }
        />
        <Route
          path={`properties`}
          element={
            <RestrictedByDocumentPermission
              permission="MANAGE_QUOTES"
              document={quote}
            >
              <ProjectQuoteProperties
                projectId={projectId}
                quoteId={quoteId}
                prefixBreadcrumbs={breadcrumbs}
                submenuItems={submenuItems}
                activeSubmenuItem={activeSubmenuItem}
                pathToDocList={`/projects/${projectId}/quotes`}
                pathToDoc={`/projects/${projectId}/quotes/${quoteId}`}
              />
            </RestrictedByDocumentPermission>
          }
        />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </ProjectQuoteContext.Provider>
  );
};
